import Hub, { CommandEvent, EventTuple } from "../hub"
import Commander from "../model/commander"
import { CommandNameToCommand } from "../model/commands"
import Command from "../model/commands/command"
import Model, { Store } from "../model/model"
import { FleetingIdea, Idea, IdeaType, Project, ProjectApiData, WebSource } from "../model/types"
import api, { API_DATE_REGEX } from "../services/api"
import assert from "../services/assert"
import log from "../services/log"
import { Sentry } from "../services/sentry"
import { Diff, Differ } from "./differ"

class CommandSyncError extends Error {
  constructor(message) {
    super(message)
    this.name = "CommandSyncError"
  }
}

class SyncError extends Error {
  constructor(message) {
    super(message)
    this.name = "SyncError"
  }
}

const POLL_INTERVAL = 5000

// TODO: put unresolvedstate into transaction so the history view (when we have it) can show what the user intended

// Keeps reference to the Store *before* the command was run. It's important to go this way because the command itself can change if later commands are merged into it.
type UnSyncedHistoryEntry = { store: Store; command: Command }

class Sync {
  id: number = Math.floor(Math.random() * 100)
  model: Model
  commander: Commander
  hub: Hub
  clock: number | null

  commandsPendingSync: UnSyncedHistoryEntry[]
  // How many of commandsPendingSync the request in flight carries. Commands
  // made meanwhile mustn't merge into those, or the server never sees them.
  commandsInFlight: number = 0
  intervalReference: number

  syncErrorCount: number = 0
  offlineAt: Date | null = null

  // The sync running now, and one queued to run after it. Calls made while a
  // sync is running share that one follow-up, so whatever was pending when
  // sync() was called has been sent by the time it resolves.
  running: Promise<void> | null = null
  queued: Promise<void> | null = null

  constructor() {
    this.clock = null

    this.commandsPendingSync = []

    window.addEventListener("beforeunload", (event) => {
      if (this.commandsPendingSync.length) {
        event.returnValue = "You have unsaved changes."
        this.immediateSync()
        return true
      }
    })
  }

  attachToCommander(commander: Commander) {
    this.commander = commander
    commander.syncCallback = (c: Command, s: Store) => this.receiveCommand(c, s)
  }

  startPolling() {
    this.stopPolling()
    this.intervalReference = window.setInterval(() => {
      this.sync()
    }, POLL_INTERVAL)
  }

  stopPolling() {
    if (this.intervalReference) clearInterval(this.intervalReference)
  }

  async immediateSync() {
    this.stopPolling()
    await this.sync()
    this.startPolling()
  }

  fetchAll(cb: () => void) {
    api()
      .get("/api/sync")
      .then(({ data }) => {
        log("SYNC", "#AFC", `fetched all to clock ${data.clock}`)
        data.ideas.forEach((idea) => this.model.createIdea(idea.idea_id, convertDataToIdea(idea)))
        data.fleeting_ideas.forEach((idea) =>
          this.model.createFleetingIdea(idea.id, convertDataToFleetingIdea(idea)),
        )
        data.sources.forEach((sourceData) =>
          this.model.createSource(sourceData.id, convertDataToSource(sourceData), []),
        )
        data.projects.forEach((projectData: ProjectApiData) =>
          this.model.createProject(projectData.id, convertDataToProject(projectData)),
        )
        cb()
        this.clock = data.clock
      })
  }

  receiveCommand(command: Command, storeBefore: Store) {
    const lastIndex = this.commandsPendingSync.length - 1
    const last =
      lastIndex >= this.commandsInFlight ? this.commandsPendingSync[lastIndex] : undefined
    const merged = command.merge(last?.command)
    if (merged === undefined) this.commandsPendingSync.push({ command, store: storeBefore })
    else this.commandsPendingSync[lastIndex].command = merged
  }

  serializeCommands(cs: Command[]): EventTuple<CommandEvent>[] {
    return cs.map((c: Command) => this.serializeCommand(c))
  }

  serializeCommand(command: Command): EventTuple<CommandEvent> {
    const action = `command:${command.name}`
    const data = command.state
    let jsonData = data
    if (action === "command:set-idea-field" && data.field === "content") {
      let diff: Diff | null = null
      const differ = new Differ()
      try {
        diff = differ.diff(data.oldValue, data.value)
      } catch (e) {
        console.log("error while diffing", e)
      }
      jsonData = { ...data, diff }
    }
    return [`command:${command.name}`, deepFormatDates(jsonData)]
  }

  deserializeCommands(tuples: EventTuple<CommandEvent>[]): Command[] {
    return tuples.map((tuple: EventTuple<CommandEvent>) => {
      return this.deserializeCommand(tuple)
    })
  }

  deserializeCommand([name, state]: EventTuple<CommandEvent>): Command {
    const klass = CommandNameToCommand[name]
    if (!klass) throw new Error(`Unknown command name ${name}`)
    return new klass(deepParseDates(state))
  }

  sync(): Promise<void> {
    if (this.running) {
      if (!this.queued)
        this.queued = this.running.then(() => {
          this.queued = null
          return this.sync()
        })
      return this.queued
    }
    this.running = this.syncNow().finally(() => {
      this.running = null
    })
    return this.running
  }

  private async syncNow(): Promise<void> {
    if (!navigator.onLine) {
      if (!this.offlineAt) this.offlineAt = new Date()
      this.hub.emit("sync:online-status", {
        online: navigator.onLine,
        offlineAt: this.offlineAt,
        syncErrorCount: this.syncErrorCount,
      })
      return
    }
    if (navigator.onLine) {
      this.offlineAt = null
    }
    if (this.syncErrorCount == 6 && this.commandsPendingSync.length > 0) {
      window.alert("We've been unable to save your changes for a minute or so. We'll keep trying.")
    }

    this.stopPolling()
    const localCount = this.commandsPendingSync.length
    const cleanStore = localCount > 0 ? this.commandsPendingSync[0].store : this.model.store

    const commandsPendingSync = this.commandsPendingSync.map(({ command }) => command)

    const eventTuples = this.serializeCommands(commandsPendingSync)

    try {
      this.commandsInFlight = localCount
      const response = await fetch(`/api/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events: eventTuples, clock: this.clock }),
      })

      if (!response.ok) throw new Error(`HTTP error ${response.status}`)

      const data = await response.json()
      assert(["done", "pending"].includes(data.status))
      assert(data.clock != null)

      switch (data.status) {
        case "done":
          // Keep anything made while the request was in flight.
          this.commandsPendingSync = this.commandsPendingSync.slice(localCount)
          this.commandsInFlight = 0
          this.clock = data.clock
          if (data.id_mappings) this.model.replaceIds(data.id_mappings)
          data.errors.forEach((result: { error: string; tuple: EventTuple<CommandEvent> }) => {
            console.error("Error syncing change:", data.errors)
            Sentry.withScope((scope) => {
              scope.setTag("sync.action", result.tuple[0])
              scope.setContext("sync", {
                action: result.tuple[0],
                message: result.error,
              })
              Sentry.captureException(new CommandSyncError(result.tuple[0]))
            })
          })
          if (data.errors.length)
            window.alert(
              "There was an error saving a change you made in the last few seconds. Please reload the page.",
            )
          break
        case "pending":
          assert(data.transactions.length > 0)

          this.model.store = cleanStore
          this.commandsInFlight = 0

          const remoteCommands = this.deserializeCommands(
            data.transactions.map(({ action, data }) => [action, data] as EventTuple<CommandEvent>),
          )

          remoteCommands.forEach((c) => this.commander.execWithoutHistoryOrSync(c))
          // Replay ours on top, including any made while the request was in
          // flight. Each gets a fresh snapshot of the store before it, so a
          // second rebase rewinds to a store that has these remote changes.
          // Commands that conflict with the remote changes are dropped.
          this.commandsPendingSync = this.commandsPendingSync.filter((entry) => {
            entry.store = this.model.store
            return this.commander.execWithoutHistoryOrSync(entry.command)
          })
          this.clock = data.clock
          await this.syncNow()
          break
      }
      this.hub.emit("sync:complete", {})
      this.syncErrorCount = 0
      this.hub.emit("sync:online-status", {
        online: navigator.onLine,
        offlineAt: this.offlineAt,
        syncErrorCount: this.syncErrorCount,
      })
      this.startPolling()
    } catch (error) {
      this.commandsInFlight = 0
      console.error("Error in /api/sync:", error)
      if (!navigator.onLine) {
        if (!this.offlineAt) this.offlineAt = new Date()
      } else {
        // @ts-expect-error
        const error2 = new SyncError(error.message)
        Sentry.captureException(error2)
        this.syncErrorCount++
      }
      this.hub.emit("sync:online-status", {
        online: navigator.onLine,
        offlineAt: this.offlineAt,
        syncErrorCount: this.syncErrorCount,
      })
      this.startPolling()
    }
  }
}

const convertDataToIdea = (data: any): Idea => {
  const { idea_id, gx, gy, colour, title, emoji } = data
  const createdAt = parseApiDate(data.created_at)
  const updatedAt = parseApiDateOrNone(data.updated_at) || createdAt
  const editedAt = parseApiDateOrNone(data.edited_at) || updatedAt
  const lastViewedAt = parseApiDateOrNone(data.last_viewed_at)
  return {
    id: idea_id,
    type: data.type as IdeaType,
    colour,
    title,
    memo: emoji,
    content: data.content,
    sourceType: data.source_type,
    sourceId: data.source_id,
    location: { gx, gy },
    connections: data.connections,
    createdAt,
    updatedAt,
    editedAt,
    lastViewedAt: lastViewedAt || null,
  }
}

const deepFormatDates = (data: any): any => {
  const result: any = {}
  for (const key in data)
    if (data[key] instanceof Date) result[key] = data[key].toISOString()
    else if (typeof data[key] === "object" && Array.isArray(data[key])) result[key] = data[key]
    else if (typeof data[key] === "object" && data[key] !== null)
      result[key] = deepFormatDates(data[key])
    else result[key] = data[key]
  return result
}

const deepParseDates = (data: any): any => {
  const result: any = {}
  for (const key in data) {
    if (typeof data[key] === "string" && data[key].match(API_DATE_REGEX))
      result[key] = new Date(Date.parse(data[key]))
    else if (typeof data[key] === "object" && Array.isArray(data[key])) result[key] = data[key]
    else if (typeof data[key] === "object" && data[key] !== null)
      result[key] = deepParseDates(data[key])
    else result[key] = data[key]
  }
  return result
}

const convertDataToFleetingIdea = (data: any): FleetingIdea => {
  const createdAt = parseApiDate(data.created_at)
  return {
    id: data.id,
    content: data.content,
    sourceId: data.source_id,
    createdAt,
  }
}

const parseApiDateOrNone = (date: string | undefined): Date | undefined =>
  date ? new Date(Date.parse(date)) : undefined

const parseApiDate = (date: string): Date => new Date(Date.parse(date))

const convertDataToSource = (data: any): WebSource => {
  const publicationDate = parseApiDateOrNone(data.publication_date)
  const createdAt = parseApiDate(data.created_at)
  const updatedAt = parseApiDate(data.updated_at)
  const lastUsedAt = parseApiDateOrNone(data.last_used_at)
  const lastReadAt = parseApiDateOrNone(data.last_read_at)
  const scannedAt = parseApiDateOrNone(data.scanned_at)
  return {
    id: data.id,
    url: data.url,
    name: data.name,
    userProvidedName: data.user_provided_name,
    scannedName: data.scanned_name,
    author: data.author,
    publicationDate: publicationDate,
    type: data.type,
    status: data.status,
    createdAt: createdAt,
    updatedAt: updatedAt,
    lastUsedAt: lastUsedAt,
    lastReadAt: lastReadAt,
    scannedAt: scannedAt,
    metadataError: data.metadata_error,
    goalId: data.goal_id,
    literatureNotes: data.literature_notes,
    summary: data.summary,
    iframeForbidden: data.iframe_forbidden,
    hasStoredContent: data.has_stored_content,
    contentFileContentType: data.content_file_content_type,
    hasStoredMarkdown: data.has_stored_markdown,
  }
}

const convertDataToProject = (data: ProjectApiData): Project => {
  const createdAt = parseApiDate(data.created_at)
  const updatedAt = parseApiDate(data.updated_at)
  const completedAt = parseApiDateOrNone(data.completed_at)
  return {
    id: data.id,
    name: data.name,
    status: data.status,
    completedAt: completedAt,
    goalId: data.goal_id,
    createdAt: createdAt,
    updatedAt: updatedAt,
    ideaIds: data.idea_ids,
  }
}

export default Sync
